
import React from 'react';
import { 
  LayoutDashboard, 
  Users, 
  ShoppingBag, 
  CreditCard, 
  Settings, 
  PieChart, 
  HelpCircle,
  Package,
  FileText,
  Quote,
  UserCircle,
  Globe,
  Hash,
  Building2,
  Database,
  UserCheck,
  CalendarDays,
  Wrench,
  ScrollText,
  CalendarClock,
  ClipboardList,
  PackageCheck,
  MessageSquareWarning
} from 'lucide-react';
import { NavItem, StatItem, Transaction } from './types';

// Order: Dashboard > Domain > Leads > Quotation > Orders > Database (Orgs/Customers/Contacts) > Reports, then rest
// Permission codes must match HRMS /api/rbac/user/info/
// marketing.view_database = show if user has any of view_organization, view_customer, view_contact (handled in Sidebar)
export const SIDEBAR_LINKS: NavItem[] = [
  { title: 'Dashboard', icon: LayoutDashboard, href: '/', permission: undefined },
  { title: 'Domains', icon: Globe, href: '/domains', permission: 'rbac.view_domain_tab' },
  { title: 'Leads', icon: Users, href: '/leads', permission: 'marketing.view_lead' },
  { title: 'Quotations', icon: Quote, href: '/quotations', permission: 'marketing.view_lead' },
  { title: 'Orders', icon: Package, href: '/orders', permission: 'marketing.view_lead' },
  { title: 'Database', icon: Database, href: '/database', permission: 'marketing.view_database' },
  { title: 'My Team', icon: UserCheck, href: '/my-team', permission: 'marketing.view_myteam' },
  { title: 'Events', icon: CalendarDays, href: '/events', permission: 'marketing.view_events' },
  { title: 'MIS', icon: PieChart, href: '/reports', permission: 'marketing.view_report' },
  // { title: 'Report templates', icon: FileText, href: '/report-templates', permission: 'marketing.view_lead' },
  // { title: 'Employees', icon: Users, href: '/employees', permission: 'marketing.view_domain' },
];

/**
 * Service module — sidebar group. Rendered as a collapsible section in Sidebar.tsx.
 * The whole group is gated on `service.view`; per-page action perms are
 * enforced on the routes and inside each page. Stage 1 ships Contracts only —
 * the rest are placeholders for later stages.
 */
export const SERVICE_GROUP_PERMISSION = 'service.view';
export const SERVICE_LINKS: NavItem[] = [
  { title: 'Contracts', icon: ScrollText, href: '/service/contracts', permission: 'service.view' },
  { title: 'Service Plan', icon: CalendarClock, href: '/service/visits', permission: 'service.view' },
  { title: 'Work Orders', icon: ClipboardList, href: '/service/work-orders', permission: 'service.view' },
  { title: 'Store / Dispatch', icon: PackageCheck, href: '/service/store', permission: 'service.view' },
  { title: 'Complaints', icon: MessageSquareWarning, href: '/service/complaints', permission: 'service.view' },
  { title: 'Reports', icon: PieChart, href: '/service/reports', permission: 'service.view' },
];

/** localStorage key for the default lead number series (assign from Leads page "Number series" button). */
export const DEFAULT_LEAD_SERIES_STORAGE_KEY = 'marketing_default_lead_series_code';

export const SECONDARY_LINKS: NavItem[] = [
  { title: 'Settings', icon: Settings, href: '/settings' },
  { title: 'Support & Tickets', icon: HelpCircle, href: '/support' },
];

// Dashboard stats are now loaded dynamically in DashboardPage (marketing API)
export const DASHBOARD_STATS: StatItem[] = [];

export const RECENT_TRANSACTIONS: Transaction[] = [
  { id: '1', customer: 'Liam Johnson', email: 'liam@example.com', amount: '$250.00', status: 'Completed', date: '2023-06-23' },
  { id: '2', customer: 'Olivia Smith', email: 'olivia@example.com', amount: '$150.00', status: 'Pending', date: '2023-06-24' },
  { id: '3', customer: 'Noah Williams', email: 'noah@example.com', amount: '$350.00', status: 'Completed', date: '2023-06-25' },
  { id: '4', customer: 'Emma Brown', email: 'emma@example.com', amount: '$450.00', status: 'Canceled', date: '2023-06-26' },
  { id: '5', customer: 'James Wilson', email: 'james@example.com', amount: '$550.00', status: 'Completed', date: '2023-06-27' },
];

export const CHART_DATA = [
  { name: 'Jan', revenue: 4000, target: 2400 },
  { name: 'Feb', revenue: 3000, target: 1398 },
  { name: 'Mar', revenue: 2000, target: 9800 },
  { name: 'Apr', revenue: 2780, target: 3908 },
  { name: 'May', revenue: 1890, target: 4800 },
  { name: 'Jun', revenue: 2390, target: 3800 },
  { name: 'Jul', revenue: 3490, target: 4300 },
];

// Name title/salutation options (displayed before person name everywhere)
export const NAME_PREFIXES: { value: string; label: string }[] = [
  { value: '', label: '—' },
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Miss', label: 'Miss' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Prof.', label: 'Prof.' },
  { value: 'Eng.', label: 'Eng.' },
  { value: 'Sir', label: 'Sir' },
  { value: 'Madam', label: 'Madam' },
];

// Country calling codes with flags (from country-list-with-dial-code-and-flag)
export { COUNTRY_CODES, DEFAULT_COUNTRY_CODE, getCountryCodeSearchText } from './lib/country-codes';

// Indian states and union territories (for address state dropdown)
export const INDIAN_STATES: { value: string; label: string }[] = [
  { value: 'Andhra Pradesh', label: 'Andhra Pradesh' },
  { value: 'Arunachal Pradesh', label: 'Arunachal Pradesh' },
  { value: 'Assam', label: 'Assam' },
  { value: 'Bihar', label: 'Bihar' },
  { value: 'Chhattisgarh', label: 'Chhattisgarh' },
  { value: 'Goa', label: 'Goa' },
  { value: 'Gujarat', label: 'Gujarat' },
  { value: 'Haryana', label: 'Haryana' },
  { value: 'Himachal Pradesh', label: 'Himachal Pradesh' },
  { value: 'Jharkhand', label: 'Jharkhand' },
  { value: 'Karnataka', label: 'Karnataka' },
  { value: 'Kerala', label: 'Kerala' },
  { value: 'Madhya Pradesh', label: 'Madhya Pradesh' },
  { value: 'Maharashtra', label: 'Maharashtra' },
  { value: 'Manipur', label: 'Manipur' },
  { value: 'Meghalaya', label: 'Meghalaya' },
  { value: 'Mizoram', label: 'Mizoram' },
  { value: 'Nagaland', label: 'Nagaland' },
  { value: 'Odisha', label: 'Odisha' },
  { value: 'Punjab', label: 'Punjab' },
  { value: 'Rajasthan', label: 'Rajasthan' },
  { value: 'Sikkim', label: 'Sikkim' },
  { value: 'Tamil Nadu', label: 'Tamil Nadu' },
  { value: 'Telangana', label: 'Telangana' },
  { value: 'Tripura', label: 'Tripura' },
  { value: 'Uttar Pradesh', label: 'Uttar Pradesh' },
  { value: 'Uttarakhand', label: 'Uttarakhand' },
  { value: 'West Bengal', label: 'West Bengal' },
  { value: 'Andaman and Nicobar Islands', label: 'Andaman and Nicobar Islands' },
  { value: 'Chandigarh', label: 'Chandigarh' },
  { value: 'Dadra and Nagar Haveli and Daman and Diu', label: 'Dadra and Nagar Haveli and Daman and Diu' },
  { value: 'Delhi', label: 'Delhi' },
  { value: 'Jammu and Kashmir', label: 'Jammu and Kashmir' },
  { value: 'Ladakh', label: 'Ladakh' },
  { value: 'Lakshadweep', label: 'Lakshadweep' },
  { value: 'Puducherry', label: 'Puducherry' },
];

// Industry options for organizations
export const INDUSTRY_OPTIONS: { value: string; label: string }[] = [
  { value: 'end user', label: 'End User' },
  { value: 'distributor/dealer', label: 'Distributor / Dealer' },
  { value: 'consultant', label: 'Consultant' },
];
